import { EvaluationColor } from '@repo/types';
import { DivergingBar, getSvgByName } from '@repo/ui';
import {
  AgeGroup,
  getAgeGroup,
  getAgeInYears,
  MICROBIOME_AGE_CATEGORY_KEY,
} from '@repo/utils';
import {
  Badge,
  Card,
  CardErrorBoundaryWithSpinfinity,
  Icon,
  Typography,
} from 'components';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import useFeature from 'services/features/useFeature';
import useSubMetricEvaluations from 'services/insights/useSubMetricEvaluations';
import { formatMicrobiomeAgeDelta } from 'services/microbiome/formatMicrobiomeAgeDelta';
import styles from './MicrobiomeAgeCard.module.css';

const colorMapping: Record<
  EvaluationColor,
  Parameters<typeof Typography>[0]['color']
> = {
  green: 'darkGreen',
  grey: 'grey',
  red: 'darkRed',
  orange: 'darkOrange',
};

const RANGE_BAR_MIN = -30;

export default function MicrobiomeAgeCard() {
  const { isFeatureEnabled } = useFeature('microbiome_age');
  const { currentKitId, currentKit } = useCurrentKitAndTinyAccount();
  const { data: metrics } = useSubMetricEvaluations();

  if (!isFeatureEnabled) return null;

  const isAdult =
    getAgeGroup(currentKit?.age_at_sampling ?? 0) === AgeGroup.ADULT;

  if (!isAdult) return null;

  const submetric = metrics.find(
    m => m.category_key === MICROBIOME_AGE_CATEGORY_KEY,
  );

  if (!submetric) return null;

  const userValue = submetric.user_value ?? 0;
  const ageAtSampling = currentKit?.age_at_sampling;
  const delta =
    ageAtSampling != null
      ? formatMicrobiomeAgeDelta(ageAtSampling, userValue)
      : undefined;
  // Plot the predicted age relative to the chronological age: negative (younger)
  // fills green to the left, positive (older) fills red to the right.
  const barValue =
    ageAtSampling != null ? userValue - getAgeInYears(ageAtSampling) : 0;

  return (
    <CardErrorBoundaryWithSpinfinity cardName='MicrobiomeAgeCard'>
      <Card
        clickable={{
          href: `/results/${currentKitId}/metrics/all-metrics/${submetric?.sample_health_id}`,
          text: 'Microbiome age',
          isExternal: false,
        }}
      >
        <div className={styles.container}>
          <div className={styles.header}>
            <Typography variant='heading-s'>Microbiome age</Typography>
            <Badge title='BETA' state='new' />
            <Icon
              Svg={getSvgByName('chevronForward')}
              size='s'
              className={styles.icon}
            />
          </div>
          <div className={styles.body}>
            <Typography variant='heading-xl'>
              {Math.floor(+userValue + 0.5)}
            </Typography>
            <Typography
              variant='caption'
              color={colorMapping[submetric.evaluation_color]}
            >
              {delta}
            </Typography>
            <DivergingBar
              min={RANGE_BAR_MIN}
              max={Math.abs(RANGE_BAR_MIN)}
              value={barValue}
              color={submetric.evaluation_color}
              className={styles.bar}
            />
            <Typography variant='caption' color='grey'>
              Current age
            </Typography>
          </div>
        </div>
      </Card>
    </CardErrorBoundaryWithSpinfinity>
  );
}
